import React, { useState, useRef, useEffect } from 'react';
import { Share2, X, Facebook, Twitter, Linkedin, Link as LinkIcon, Check, MessageCircle } from 'lucide-react';

interface ShareButtonProps {
  url: string;
  title: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg';
  align?: 'left' | 'right';
}

const ShareButton: React.FC<ShareButtonProps> = ({ url, title, description = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const buttonRef = useRef<HTMLButtonElement>(null);

  const fullUrl = typeof window !== 'undefined'
    ? `${window.location.origin}${url}`
    : url;

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullUrl)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(fullUrl)}&text=${encodeURIComponent(title)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(fullUrl)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${title} - ${fullUrl}`)}`,
  };

  const openDropdown = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = Math.min(288, window.innerWidth - 16);
      let left = rect.right - dropdownWidth;
      if (left < 8) left = 8;
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 8,
        left,
        width: dropdownWidth,
        zIndex: 9999,
      });
    }
    setIsOpen(prev => !prev);
  };

  useEffect(() => {
    const handleResize = () => { if (isOpen) openDropdown(); };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
    } catch {
      const el = document.createElement('textarea');
      el.value = fullUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        onClick={openDropdown}
        className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm font-medium"
        aria-label="Share"
      >
        <Share2 className="w-4 h-4 flex-shrink-0" />
        <span>Share</span>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />
          <div style={dropdownStyle} className="bg-white rounded-xl shadow-2xl border border-gray-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <span className="font-semibold text-gray-900 text-sm">Share this</span>
              <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div className="grid grid-cols-4 gap-2">
                <a href={shareLinks.facebook} target="_blank" rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1 p-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition">
                  <Facebook className="w-5 h-5" />
                  <span className="text-xs font-medium">Facebook</span>
                </a>
                <a href={shareLinks.twitter} target="_blank" rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1 p-3 rounded-lg bg-sky-500 text-white hover:bg-sky-600 transition">
                  <Twitter className="w-5 h-5" />
                  <span className="text-xs font-medium">Twitter</span>
                </a>
                <a href={shareLinks.linkedin} target="_blank" rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1 p-3 rounded-lg bg-blue-700 text-white hover:bg-blue-800 transition">
                  <Linkedin className="w-5 h-5" />
                  <span className="text-xs font-medium">LinkedIn</span>
                </a>
                <a href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1 p-3 rounded-lg bg-green-500 text-white hover:bg-green-600 transition">
                  <MessageCircle className="w-5 h-5" />
                  <span className="text-xs font-medium">WhatsApp</span>
                </a>
              </div>

              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                <span className="flex-1 text-xs text-gray-500 truncate min-w-0">{fullUrl}</span>
                <button
                  onClick={copyToClipboard}
                  className={`flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition ${
                    copied
                      ? 'bg-green-100 text-green-700 border border-green-300'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <LinkIcon className="w-3.5 h-3.5" />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ShareButton;
